import { type DocumentNode, gql } from '@apollo/client';

/**
 * Query variables for assetsByCollectionId
 */
export interface AssetsByCollectionIdVariables {
  id: string;
  first?: number;
  after?: string;
  q?: string;
  search?: string;
}

/**
 * Field definition structure
 */
export interface FieldDefinition {
  id: string;
  name: string;
  fieldType: string;
}

/**
 * Custom field node structure
 */
export interface CustomFieldNode {
  id: string;
  definition: FieldDefinition;
  content: string;
}

/**
 * Custom field edge wrapper
 */
export interface CustomFieldEdge {
  node: CustomFieldNode;
}

/**
 * Custom field set connection
 */
export interface CustomFieldSetConnection {
  edges: CustomFieldEdge[];
}

/**
 * Format node structure
 */
export interface FormatNode {
  id: string;
  mediapath: string;
  filename: string;
  formatType: string;
}

/**
 * Format edge wrapper
 */
export interface FormatEdge {
  node: FormatNode;
}

/**
 * Page info for format pagination
 */
export interface FormatPageInfo {
  startCursor: string | null;
  endCursor: string | null;
}

/**
 * Format set connection
 */
export interface FormatSetConnection {
  pageInfo: FormatPageInfo;
  edges: FormatEdge[];
}

/**
 * Asset node type from collection items
 */
export interface AssetNodeType {
  __typename: 'AssetNodeType';
  id: string;
  name: string;
  myAssetPermissions: string[];
  createdAt: string;
  clipId: string | null;
  legacySysId: string | null;
  inpoint: number | null;
  outpoint: number | null;
  duration: number | null;
  timecode: string | null;
  language: string | null;
  mediasize: number | null;
  mediatype: string | null;
  jsonMetadata: string | null;
  dateAdded: string | null;
  lastModified: string | null;
  customfieldSet: CustomFieldSetConnection;
  formatSet: FormatSetConnection;
}

/**
 * Content object union type
 */
export type ContentObject = AssetNodeType;

/**
 * Collection item node structure
 */
export interface CollectionItemNode {
  id: string;
  contentObject: ContentObject;
}

/**
 * Collection item edge wrapper
 */
export interface CollectionItemEdge {
  cursor: string;
  node: CollectionItemNode;
}

/**
 * Page info for collection items pagination
 */
export interface CollectionPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

/**
 * Collection items connection
 */
export interface CollectionItemsConnection {
  pageInfo: CollectionPageInfo;
  totalCount: number;
  edges: CollectionItemEdge[];
}

/**
 * Collection structure
 */
export interface Collection {
  id: string;
  name: string;
  items: CollectionItemsConnection;
}

/**
 * Response structure for assetsByCollectionId query
 */
export interface AssetsByCollectionIdResponse {
  collection: Collection | null;
}

const assetsByCollectionId: DocumentNode = gql`
  query collection($id: ID!, $first: Int, $after: String, $q: String, $search: String) {
    collection(id: $id) {
      id
      name
      items(first: $first, after: $after, q: $q, search: $search) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
        edges {
          cursor
          node {
            id
            contentObject {
              __typename
              ... on AssetNodeType {
                id
                name
                myAssetPermissions
                createdAt
                clipId
                legacySysId
                inpoint
                outpoint
                duration
                timecode
                language
                mediasize
                mediatype
                jsonMetadata
                dateAdded
                createdAt
                lastModified
                customfieldSet {
                  edges {
                    node {
                      id
                      definition {
                        id
                        name
                        fieldType
                      }
                      content
                    }
                  }
                }
                formatSet {
                  pageInfo {
                    startCursor
                    endCursor
                  }
                  edges {
                    node {
                      id
                      mediapath
                      filename
                      formatType
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default assetsByCollectionId;
